import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  VStack,
  Select,
  Text,
  Button,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Switch,
  Flex,
  useColorModeValue,
  HStack,
  Icon,
  Divider,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  FormHelperText,
} from '@chakra-ui/react';
import { FaStore, FaKey, FaLink, FaCogs, FaExchangeAlt, FaMagic } from 'react-icons/fa';
import { useMutation, useQueryClient } from 'react-query';
import { createGateway, updateGateway, exchangeDigikalaToken, getBasalamCustomerId } from '../../services/gatewayService';
import { formatDateShamsi } from '../../utils/date';
import toast from 'react-hot-toast';

function generateRandomString(length = 32) {
  try {
    if (window.crypto && window.crypto.getRandomValues) {
      const bytes = new Uint8Array(length);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .slice(0, length);
    }
  } catch (e) {}
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const GatewayForm = forwardRef(({ gateway, onSuccess, onDirty }, ref) => {
  const queryClient = useQueryClient();
  const [gatewayType, setGatewayType] = useState(gateway?.type || '');
  const [digikalaAuthMode, setDigikalaAuthMode] = useState(() => {
    if (!gateway) return 'tokens';
    if (gateway?.digikalaAccessToken && gateway?.digikalaRefreshToken) return 'tokens';
    return 'verification';
  });
  const [digikalaVerificationCode, setDigikalaVerificationCode] = useState('');
  const [digikalaTokenLoading, setDigikalaTokenLoading] = useState(false);
  const [basalamCustomerIdLoading, setBasalamCustomerIdLoading] = useState(false);

  // 🎨 متغیرهای رنگی (طراحی SaaS)
  const inputBg = useColorModeValue("gray.50", "gray.900");
  const sectionBg = useColorModeValue("white", "whiteAlpha.50");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const textColor = useColorModeValue("gray.800", "white");

  const commonInputProps = {
    bg: inputBg,
    border: "1px solid",
    borderColor: "transparent",
    borderRadius: "xl",
    _focus: {
      borderColor: "blue.400",
      boxShadow: "0 0 0 1px #3b82f6",
      bg: useColorModeValue("white", "gray.800")
    },
    _hover: {
      borderColor: useColorModeValue("gray.300", "gray.600")
    },
    transition: "all 0.2s"
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      name: gateway?.name || '',
      type: gateway?.type || '',
      basalamPanelId: gateway?.basalamPanelId || '',
      basalamStoreId: gateway?.basalamStoreId || '',
      basalamAccessToken: gateway?.basalamAccessToken || '',
      basalamRefreshToken: gateway?.basalamRefreshToken || '',
      digikalaSellerCode: gateway?.digikalaSellerCode || '',
      digikalaClientCode: gateway?.digikalaClientCode || '',
      digikalaPrivateKey: gateway?.digikalaPrivateKey || '',
      digikalaPublicKey: gateway?.digikalaPublicKey || '',
      digikalaAccessToken: '',
      digikalaRefreshToken: '',
      customSiteUrl: gateway?.customSiteUrl || '',
      customCallbackUrl: gateway?.customCallbackUrl || '',
      customStockUpdateUrl: gateway?.customStockUpdateUrl || '',
      customProductsUrl: gateway?.customProductsUrl || '',
      customWebhookApiKey: gateway?.customWebhookApiKey || '',
      customWebhookSecret: gateway?.customWebhookSecret || '',
      isActive: gateway?.isActive !== undefined ? gateway.isActive : true,
      syncPrice: gateway?.syncPrice !== undefined ? gateway.syncPrice : true,
      pricePercent: gateway?.pricePercent ?? 0,
    },
    mode: 'onChange',
  });

  const watchedType = watch('type');

  useEffect(() => {
    setGatewayType(watchedType);
    if (watchedType === 'digikala' && !gateway) {
      setDigikalaAuthMode('tokens');
    }
    if (watchedType === 'custom' && !gateway) {
      const currentApiKey = watch('customWebhookApiKey');
      const currentSecret = watch('customWebhookSecret');
      if (!currentApiKey) setValue('customWebhookApiKey', generateRandomString(24));
      if (!currentSecret) setValue('customWebhookSecret', generateRandomString(64));
    }
  }, [watchedType]);

  const createGatewayMutation = useMutation(createGateway, {
    onSuccess: (response) => {
      const createdGateway = response?.data;
      toast.success('درگاه با موفقیت اضافه شد');
      queryClient.invalidateQueries(['gateways']);
      if (onSuccess) onSuccess(createdGateway);
    },
    onError: (error) => toast.error(error.response?.data?.message || 'خطا در افزودن درگاه')
  });

  const updateGatewayMutation = useMutation(
    ({ gatewayId, gatewayData }) => updateGateway(gatewayId, gatewayData),
    {
      onSuccess: () => {
        toast.success('درگاه با موفقیت بروزرسانی شد');
        queryClient.invalidateQueries(['gateways']);
        if (onSuccess) onSuccess(null);
      },
      onError: (error) => toast.error(error.response?.data?.message || 'خطا در بروزرسانی درگاه')
    }
  );

  const handleFormSubmit = (data) => {
    const cleanData = { ...data };

    if (cleanData.type === 'basalam') {
      delete cleanData.digikalaSellerCode; delete cleanData.digikalaPrivateKey; delete cleanData.digikalaPublicKey;
      delete cleanData.customSiteUrl; delete cleanData.customCallbackUrl; delete cleanData.customStockUpdateUrl; delete cleanData.customProductsUrl; delete cleanData.customWebhookApiKey; delete cleanData.customWebhookSecret;
    } else if (cleanData.type === 'digikala') {
      delete cleanData.basalamPanelId; delete cleanData.basalamStoreId; delete cleanData.basalamAccessToken; delete cleanData.basalamRefreshToken;
      delete cleanData.customSiteUrl; delete cleanData.customCallbackUrl; delete cleanData.customStockUpdateUrl; delete cleanData.customProductsUrl; delete cleanData.customWebhookApiKey; delete cleanData.customWebhookSecret;
      const accessTokenTrimmed = cleanData.digikalaAccessToken?.trim?.() || '';
      const refreshTokenTrimmed = cleanData.digikalaRefreshToken?.trim?.() || '';

      if (!accessTokenTrimmed) delete cleanData.digikalaAccessToken;
      if (!refreshTokenTrimmed) delete cleanData.digikalaRefreshToken;
      delete cleanData.digikalaAccessTokenExpiresAt; delete cleanData.digikalaRefreshTokenExpiresAt;

      if (digikalaAuthMode === 'tokens') {
        delete cleanData.digikalaClientCode; delete cleanData.digikalaPrivateKey; delete cleanData.digikalaPublicKey;
        if (!cleanData.digikalaSellerCode?.trim?.()) delete cleanData.digikalaSellerCode;
      } else {
        if (!cleanData.digikalaSellerCode?.trim?.()) delete cleanData.digikalaSellerCode;
        if (!cleanData.digikalaClientCode?.trim?.()) delete cleanData.digikalaClientCode;
      }
    } else if (cleanData.type === 'custom') {
      delete cleanData.basalamPanelId; delete cleanData.basalamStoreId; delete cleanData.basalamAccessToken; delete cleanData.basalamRefreshToken;
      delete cleanData.digikalaSellerCode; delete cleanData.digikalaPrivateKey; delete cleanData.digikalaPublicKey;
      delete cleanData.digikalaClientCode; delete cleanData.digikalaAccessToken; delete cleanData.digikalaRefreshToken;
      delete cleanData.digikalaAccessTokenExpiresAt; delete cleanData.digikalaRefreshTokenExpiresAt;
    }

    cleanData.syncPrice = cleanData.syncPrice !== false;
    const percent = Number(cleanData.pricePercent);
    cleanData.pricePercent = Number.isFinite(percent) ? percent : 0;

    if (gateway) updateGatewayMutation.mutate({ gatewayId: gateway._id, gatewayData: cleanData });
    else createGatewayMutation.mutate(cleanData);
  };

  const handleExchangeDigikalaToken = async () => {
    if (!gateway?._id) {
      toast.error('ابتدا درگاه را ذخیره کنید، سپس کد اعتبارسنجی را وارد و دکمه دریافت توکن را بزنید.');
      return;
    }
    const code = digikalaVerificationCode.trim();
    if (!code) { toast.error('کد اعتبارسنجی را وارد کنید'); return; }
    
    setDigikalaTokenLoading(true);
    try {
      await exchangeDigikalaToken(gateway._id, code);
      toast.success('توکن‌ها با موفقیت دریافت و ذخیره شد');
      setDigikalaVerificationCode('');
      queryClient.invalidateQueries(['gateways']);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'خطا در دریافت توکن از دیجی‌کالا');
    } finally {
      setDigikalaTokenLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(handleFormSubmit)()
  }));

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} onChange={() => onDirty?.()}>
      <VStack spacing={6} align="stretch" pb={4}>
        
        {/* ========================================== */}
        {/* بخش 1: اطلاعات پایه */}
        {/* ========================================== */}
        <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
          <HStack mb={4}>
            <Icon as={FaStore} color="blue.500" />
            <Text fontWeight="bold" fontSize="md" color={textColor}>اطلاعات پایه درگاه</Text>
          </HStack>
          <VStack spacing={4}>
            <FormControl isInvalid={errors.name}>
              <FormLabel fontSize="sm" fontWeight="bold">نام درگاه</FormLabel>
              <Input
                {...commonInputProps}
                {...register('name', { required: 'نام درگاه الزامی است' })}
                placeholder="مثال: غرفه اصلی باسلام، سایت شخصی و..."
              />
              <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={errors.type}>
              <FormLabel fontSize="sm" fontWeight="bold">نوع پلتفرم</FormLabel>
              <Select
                {...commonInputProps}
                {...register('type', { required: 'نوع درگاه الزامی است' })}
                onChange={(e) => { setValue('type', e.target.value); setGatewayType(e.target.value); }}
              >
                <option value="">انتخاب پلتفرم فروش...</option>
                <option value="digikala">دیجی‌کالا</option>
                <option value="basalam">باسلام</option>
                <option value="custom">سایت شخصی (ووکامرس / کاستوم)</option>
              </Select>
              <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </Box>

        {/* ========================================== */}
        {/* بخش 2: تنظیمات اختصاصی دیجی‌کالا */}
        {/* ========================================== */}
        {gatewayType === 'digikala' && (
          <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FaKey} color="red.500" />
              <Text fontWeight="bold" fontSize="md" color={textColor}>اتصال به پنل دیجی‌کالا</Text>
            </HStack>
            
            <FormControl mb={5}>
              <FormLabel fontSize="sm" fontWeight="bold">روش احراز هویت</FormLabel>
              <Select
                {...commonInputProps}
                value={digikalaAuthMode}
                onChange={(e) => setDigikalaAuthMode(e.target.value)}
              >
                <option value="tokens">ورود مستقیم Access & Refresh Token (ساده)</option>
                <option value="verification">دریافت توکن از طریق کد اعتبارسنجی (پیشرفته)</option>
              </Select>
            </FormControl>

            {digikalaAuthMode === 'tokens' && (
              <VStack spacing={4}>
                <FormControl isInvalid={errors.digikalaAccessToken}>
                  <FormLabel fontSize="sm" fontWeight="bold">Access Token</FormLabel>
                  <Textarea
                    {...commonInputProps}
                    rows={3} fontFamily="mono" fontSize="sm" dir="ltr"
                    {...register('digikalaAccessToken', { required: !gateway?._id ? 'Access Token الزامی است' : false })}
                    placeholder={gateway?.digikalaAccessToken ? 'برای تغییر، توکن جدید را اینجا وارد کنید' : 'eyJhbG...'}
                  />
                  <FormErrorMessage>{errors.digikalaAccessToken?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.digikalaRefreshToken}>
                  <FormLabel fontSize="sm" fontWeight="bold">Refresh Token</FormLabel>
                  <Textarea
                    {...commonInputProps}
                    rows={3} fontFamily="mono" fontSize="sm" dir="ltr"
                    {...register('digikalaRefreshToken', { required: !gateway?._id ? 'Refresh Token الزامی است' : false })}
                    placeholder={gateway?.digikalaRefreshToken ? 'برای تغییر، توکن جدید را اینجا وارد کنید' : 'def5020...'}
                  />
                  <FormErrorMessage>{errors.digikalaRefreshToken?.message}</FormErrorMessage>
                </FormControl>
                
                {gateway?.digikalaAccessToken && (
                  <Alert status="success" borderRadius="xl" bg="green.50" borderWidth="1px" borderColor="green.200">
                    <AlertIcon color="green.500" />
                    <AlertDescription fontSize="sm" color="green.800">
                      توکن‌های دیجی‌کالا برای این درگاه با موفقیت ذخیره شده‌اند.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            )}

            {digikalaAuthMode === 'verification' && (
              <VStack spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl isInvalid={errors.digikalaSellerCode}>
                    <FormLabel fontSize="sm" fontWeight="bold">کد فروشنده</FormLabel>
                    <Input {...commonInputProps} dir="ltr" {...register('digikalaSellerCode', { required: 'کد فروشنده الزامی است' })} placeholder="مانند: ABCD1" />
                    <FormErrorMessage>{errors.digikalaSellerCode?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={errors.digikalaClientCode}>
                    <FormLabel fontSize="sm" fontWeight="bold">کد کلاینت (اختیاری)</FormLabel>
                    <Input {...commonInputProps} dir="ltr" {...register('digikalaClientCode')} placeholder="کد کلاینت اختصاصی" />
                    <FormErrorMessage>{errors.digikalaClientCode?.message}</FormErrorMessage>
                  </FormControl>
                </SimpleGrid>

                <FormControl isInvalid={errors.digikalaPrivateKey}>
                  <FormLabel fontSize="sm" fontWeight="bold">کلید خصوصی (Private Key)</FormLabel>
                  <Textarea {...commonInputProps} rows={3} fontFamily="mono" fontSize="xs" dir="ltr" {...register('digikalaPrivateKey', { required: 'کلید خصوصی الزامی است' })} placeholder="-----BEGIN PRIVATE KEY-----..." />
                  <FormErrorMessage>{errors.digikalaPrivateKey?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.digikalaPublicKey}>
                  <FormLabel fontSize="sm" fontWeight="bold">کلید عمومی (Public Key)</FormLabel>
                  <Textarea {...commonInputProps} rows={3} fontFamily="mono" fontSize="xs" dir="ltr" {...register('digikalaPublicKey', { required: 'کلید عمومی الزامی است' })} placeholder="-----BEGIN PUBLIC KEY-----..." />
                  <FormErrorMessage>{errors.digikalaPublicKey?.message}</FormErrorMessage>
                </FormControl>

                {!gateway?._id ? (
                  <Alert status="info" borderRadius="xl">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      ابتدا فرم را ذخیره کنید. سپس می‌توانید با وارد کردن کد اعتبارسنجی دیجی‌کالا، توکن‌های خود را استخراج کنید.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Box w="full" pt={4}>
                    <Divider mb={4} borderColor={borderColor} />
                    <HStack mb={2}>
                      <Icon as={FaExchangeAlt} color="blue.500" />
                      <Text fontWeight="bold" fontSize="sm">دریافت توکن از طریق کد اعتبارسنجی</Text>
                    </HStack>
                    <Text fontSize="xs" color={mutedColor} mb={3}>
                      کد اعتبارسنجی را از پنل دیجی‌کالا بگیرید (اعتبار: ۴ دقیقه) و اینجا وارد کنید.
                    </Text>
                    <FormControl>
                      <Textarea
                        {...commonInputProps}
                        rows={2} fontFamily="mono" fontSize="sm" dir="ltr"
                        value={digikalaVerificationCode}
                        onChange={(e) => setDigikalaVerificationCode(e.target.value)}
                        placeholder="کد Base64 دیجی‌کالا را Paste کنید"
                      />
                    </FormControl>
                    <Button
                      mt={3} w="full" colorScheme="red"
                      onClick={handleExchangeDigikalaToken}
                      isLoading={digikalaTokenLoading}
                      borderRadius="xl"
                    >
                      استخراج و ذخیره توکن‌ها
                    </Button>
                  </Box>
                )}
              </VStack>
            )}
          </Box>
        )}

        {/* ========================================== */}
        {/* بخش 3: تنظیمات اختصاصی باسلام */}
        {/* ========================================== */}
        {gatewayType === 'basalam' && (
          <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FaStore} color="orange.500" />
              <Text fontWeight="bold" fontSize="md" color={textColor}>اتصال به غرفه باسلام</Text>
            </HStack>
            
            <Alert status="warning" borderRadius="xl" bg="orange.50" borderWidth="1px" borderColor="orange.200" mb={5}>
              <AlertIcon color="orange.500" />
              <AlertDescription fontSize="sm" color="orange.800">
                <Text fontWeight="bold" mb={1} display="inline-block" mr={2}>قانون باسلام:</Text>
                کد محصول (SKU) در باسلام باید در انتهای عنوان محصول و بدون فاصله درج شود (مثال: کیف مدل L2190).
              </AlertDescription>
            </Alert>

            <VStack spacing={4}>
              <FormControl isInvalid={errors.basalamAccessToken}>
                <FormLabel fontSize="sm" fontWeight="bold">Access Token باسلام</FormLabel>
                <Textarea
                  {...commonInputProps}
                  rows={2} fontFamily="mono" fontSize="sm" dir="ltr"
                  {...register('basalamAccessToken', { required: gatewayType === 'basalam' ? 'Access Token الزامی است' : false })}
                  placeholder="توکن دسترسی را وارد کنید"
                />
                <FormErrorMessage>{errors.basalamAccessToken?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.basalamRefreshToken}>
                <FormLabel fontSize="sm" fontWeight="bold">Refresh Token باسلام</FormLabel>
                <Textarea
                  {...commonInputProps}
                  rows={2} fontFamily="mono" fontSize="sm" dir="ltr"
                  {...register('basalamRefreshToken', { required: gatewayType === 'basalam' ? 'Refresh Token الزامی است' : false })}
                  placeholder="توکن رفرش را وارد کنید"
                />
                <FormErrorMessage>{errors.basalamRefreshToken?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.basalamPanelId}>
                <Flex justify="space-between" align="center" mb={2}>
                  <FormLabel fontSize="sm" fontWeight="bold" m={0}>آیدی غرفه (Vendor ID)</FormLabel>
                  <Button
                    size="xs" colorScheme="orange" variant="outline" borderRadius="md"
                    isLoading={basalamCustomerIdLoading}
                    isDisabled={!watch('basalamAccessToken')?.trim?.()}
                    onClick={async () => {
                      const accessToken = watch('basalamAccessToken')?.trim?.();
                      const refreshToken = watch('basalamRefreshToken')?.trim?.();
                      if (!accessToken) return;
                      setBasalamCustomerIdLoading(true);
                      try {
                        const res = await getBasalamCustomerId(accessToken, refreshToken);
                        const vendorId = res?.data?.vendorId ?? res?.vendorId;
                        const vendorIdentifier = res?.data?.vendorIdentifier ?? res?.vendorIdentifier;
                        if (vendorId != null) {
                          setValue('basalamPanelId', String(vendorId));
                          if (vendorIdentifier) setValue('basalamStoreId', String(vendorIdentifier));
                          if (res?.data?.accessToken) setValue('basalamAccessToken', res.data.accessToken);
                          if (res?.data?.refreshToken) setValue('basalamRefreshToken', res.data.refreshToken);
                          toast.success('اطلاعات غرفه با موفقیت دریافت شد');
                        } else toast.error('آیدی غرفه در پاسخ سرور نبود');
                      } catch (err) {
                        toast.error(err.response?.data?.message || err.message || 'خطا در دریافت اطلاعات غرفه');
                      } finally {
                        setBasalamCustomerIdLoading(false);
                      }
                    }}
                  >
                    یافتن خودکار از روی توکن
                  </Button>
                </Flex>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <Input
                    {...commonInputProps} dir="ltr"
                    {...register('basalamPanelId', { required: gatewayType === 'basalam' ? 'آیدی عددی غرفه الزامی است' : false })}
                    placeholder="آیدی عددی غرفه شما"
                  />
                  <Input
                    {...commonInputProps} dir="ltr"
                    {...register('basalamStoreId', { required: gatewayType === 'basalam' ? 'نام غرفه (انگلیسی) الزامی است' : false })}
                    placeholder="نام غرفه (مثلاً ka-10)"
                  />
                </SimpleGrid>
                <FormErrorMessage>{errors.basalamPanelId?.message}</FormErrorMessage>
              </FormControl>
            </VStack>
          </Box>
        )}

        {/* ========================================== */}
        {/* بخش 4: تنظیمات اختصاصی سایت شخصی (Custom) */}
        {/* ========================================== */}
        {gatewayType === 'custom' && (
          <Box bg={sectionBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FaLink} color="purple.500" />
              <Text fontWeight="bold" fontSize="md" color={textColor}>اتصال به سایت شخصی (Webhook/API)</Text>
            </HStack>
            
            <VStack spacing={4}>
              <FormControl isInvalid={errors.customSiteUrl}>
                <FormLabel fontSize="sm" fontWeight="bold">آدرس سایت</FormLabel>
                <Input
                  {...commonInputProps} dir="ltr" fontSize="sm"
                  {...register('customSiteUrl', { required: gatewayType === 'custom' ? 'این فیلد الزامی است' : false })}
                  placeholder="https://your-site.com"
                />
                <FormErrorMessage>{errors.customSiteUrl?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.customCallbackUrl}>
                <FormLabel fontSize="sm" fontWeight="bold">آدرس کال‌بک (Callback URL)</FormLabel>
                <Input
                  {...commonInputProps} dir="ltr" fontSize="sm"
                  {...register('customCallbackUrl', { required: gatewayType === 'custom' ? 'این فیلد الزامی است' : false })}
                  placeholder="https://your-site.com/api/payment/callback"
                />
                <FormErrorMessage>{errors.customCallbackUrl?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.customProductsUrl}>
                <FormLabel fontSize="sm" fontWeight="bold">آدرس دریافت محصولات (GET Products)</FormLabel>
                <Input
                  {...commonInputProps} dir="ltr" fontSize="sm"
                  {...register('customProductsUrl', { required: gatewayType === 'custom' ? 'این فیلد الزامی است' : false })}
                  placeholder="https://your-site.com/api/products"
                />
                <FormErrorMessage>{errors.customProductsUrl?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={errors.customStockUpdateUrl}>
                <FormLabel fontSize="sm" fontWeight="bold">آدرس تغییر موجودی (POST Update Stock)</FormLabel>
                <Input
                  {...commonInputProps} dir="ltr" fontSize="sm"
                  {...register('customStockUpdateUrl', { required: gatewayType === 'custom' ? 'این فیلد الزامی است' : false })}
                  placeholder="https://your-site.com/api/stock/update"
                />
                <FormErrorMessage>{errors.customStockUpdateUrl?.message}</FormErrorMessage>
              </FormControl>

              <Divider borderColor={borderColor} my={2} />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                <FormControl isInvalid={errors.customWebhookApiKey}>
                  <Flex justify="space-between" align="center" mb={1}>
                    <FormLabel fontSize="sm" fontWeight="bold" m={0}>API Key (عمومی)</FormLabel>
                    <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => setValue('customWebhookApiKey', generateRandomString(24))}>تولید مجدد</Button>
                  </Flex>
                  <Input {...commonInputProps} dir="ltr" fontSize="sm" {...register('customWebhookApiKey')} readOnly />
                </FormControl>

                <FormControl isInvalid={errors.customWebhookSecret}>
                  <Flex justify="space-between" align="center" mb={1}>
                    <FormLabel fontSize="sm" fontWeight="bold" m={0}>HMAC Secret (محرمانه)</FormLabel>
                    <Button size="xs" variant="ghost" colorScheme="purple" onClick={() => setValue('customWebhookSecret', generateRandomString(64))}>تولید مجدد</Button>
                  </Flex>
                  <Input {...commonInputProps} dir="ltr" fontSize="sm" {...register('customWebhookSecret')} readOnly />
                </FormControl>
              </SimpleGrid>

              <Alert status="info" borderRadius="xl" mt={2}>
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  درخواست‌های ارسالی از سمت ما به سایت شما، با استفاده از الگوریتم HMAC و کلید مخفی فوق امضا خواهند شد.
                </AlertDescription>
              </Alert>
            </VStack>
          </Box>
        )}

        {/* ========================================== */}
        {/* وضعیت کلی درگاه */}
        {/* ========================================== */}
        <Box bg={useColorModeValue("green.50", "whiteAlpha.50")} p={5} borderRadius="2xl" borderWidth="1px" borderColor={useColorModeValue("green.100", "whiteAlpha.200")}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text fontWeight="bold" color={textColor} mb={1}>وضعیت درگاه</Text>
              <Text fontSize="xs" color={mutedColor}>
                با غیرفعال کردن، این درگاه در همگام‌سازی موجودی و ثبت سفارش نادیده گرفته می‌شود.
              </Text>
            </Box>
            <Switch {...register('isActive')} defaultChecked={gateway?.isActive !== false} colorScheme="green" size="lg" />
          </Flex>
        </Box>

        {/* ========================================== */}
        {/* قیمت‌گذاری خودکار محصولات روی این درگاه */}
        {/* ========================================== */}
        <Box bg={useColorModeValue("purple.50", "whiteAlpha.50")} p={5} borderRadius="2xl" borderWidth="1px" borderColor={useColorModeValue("purple.100", "whiteAlpha.200")}>
          <VStack align="stretch" spacing={4}>
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="bold" color={textColor} mb={1}>قیمت‌گذاری خودکار محصولات (حالت همگانی)</Text>
                <Text fontSize="xs" color={mutedColor}>
                  در صورت غیرفعال کردن، هیچ‌کدام از محصولات متصل به این درگاه قیمت‌گذاری نمی‌شوند و در عملیات همگام‌سازی، قیمت درگاه کاملاً دست‌نخورده باقی می‌ماند.
                </Text>
              </Box>
              <Switch {...register('syncPrice')} defaultChecked={gateway?.syncPrice !== false} colorScheme="purple" size="lg" />
            </Flex>
            <FormControl maxW="220px">
              <FormLabel fontSize="sm" fontWeight="bold">درصد قیمت همگانی (%)</FormLabel>
              <NumberInput
                value={watch('pricePercent') ?? 0}
                onChange={(_, n) => setValue('pricePercent', Number.isFinite(n) ? n : 0, { shouldDirty: true })}
                isDisabled={watch('syncPrice') === false}
              >
                <NumberInputField {...commonInputProps} dir="ltr" textAlign="center" />
              </NumberInput>
              <FormHelperText fontSize="xs">
                برای همه کالاهای این درگاه اعمال می‌شود؛ مگر اینکه در خود محصول یا تنوع درصد جدا تنظیم شده باشد. مثال: ۱۰ یعنی ده درصد بالاتر از قیمت پایه.
              </FormHelperText>
            </FormControl>
          </VStack>
        </Box>

      </VStack>
    </form>
  );
});

GatewayForm.displayName = 'GatewayForm';

export default GatewayForm;